import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import "../assets/css/app-banner.css";

export const STORES = {
  ios: { url: "https://apps.apple.com/us/app/legal-platform/id6475202489", label: "Free on the App Store" },
  android: { url: "https://play.google.com/store/apps/details?id=com.app.legalplatform&hl=en", label: "Free on Google Play" },
};

const DISMISS_KEY = "lp_app_banner_dismissed_at";
const DISMISS_DAYS = 3;

export const detectPlatform = () => {
  const ua = navigator.userAgent || "";
  if (/android/i.test(ua)) return "android";
  // iPadOS reports itself as a Mac; touch support gives it away.
  if (/iphone|ipad|ipod/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return "ios";
  return null;
};

const recentlyDismissed = () => {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY));
    return at > 0 && Date.now() - at < DISMISS_DAYS * 86400000;
  } catch {
    return false;
  }
};

// Already running as the installed PWA / inside an app shell — nothing to promote.
const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

/**
 * "Get the app" strip pinned above the site header on phones and tablets,
 * linking to the store for the visitor's platform. Hidden on desktop, inside
 * the signed-in portals (their own fixed layouts sit at the top), and for a
 * week after it is closed.
 */
const AppDownloadBanner = () => {
  const { pathname } = useLocation();
  const [platform] = useState(detectPlatform);
  const [dismissed, setDismissed] = useState(recentlyDismissed);

  const inPortal = /^\/(lawyer|user)(\/|$)/.test(pathname);
  const visible = !!platform && !dismissed && !inPortal && !isStandalone();

  // Push the fixed header and the page down by the banner's height while it shows.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("has-app-banner", visible);
    return () => root.classList.remove("has-app-banner");
  }, [visible]);

  if (!visible) return null;

  const store = STORES[platform];

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // Storage blocked — it just comes back next visit.
    }
    setDismissed(true);
  };

  return (
    <div className="app-banner" role="region" aria-label="Get the Legal Platform app">
      <button type="button" className="app-banner-close" onClick={dismiss} aria-label="Close">
        <i className="bi bi-x-lg" />
      </button>
      <img src={`${process.env.PUBLIC_URL}/favicon.png`} alt="" className="app-banner-icon" />
      <div className="app-banner-text">
        <strong>Legal Platform App</strong>
        <span>Talk to a lawyer anytime</span>
        <small>{store.label}</small>
      </div>
      <a className="app-banner-install" href={store.url} target="_blank" rel="noopener noreferrer">
        Install
      </a>
    </div>
  );
};

export default AppDownloadBanner;
